import { DATA_BINDING } from '@wix/app-definition-ids'
import { dynamicPageCustomRouterCreatePageClicked } from '@wix/bi-logger-wix-code-data/v2'
import type { PageRef } from '@wix/editor-platform-sdk-types'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type {
  AppApi,
  DpBiDefaults,
  EDITOR_TYPE,
} from '@wix/wix-data-client-common'
import {
  addOrUpdateRouterWithPattern,
  getCollectionType,
  PAGE_TYPES,
  runTransactionWithRetry,
  UNDO_REDO_LABELS,
  DYNAMIC_PAGE_TYPES,
  setDynamicPageTypeStateForPage,
} from '@wix/wix-data-client-common'
import type {
  EditorSDK,
  FedopsLogger,
  I18n,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import type Experiments from '@wix/wix-experiments'
import {
  getProgressBar,
  openPagesPanelIfNeeded,
} from '../../progress-bar/executeSteps'
import { afterAddingPages as addPageLinkFields } from '../add-dynamic-pages/dpCreationSteps'
import { addRouterDataset } from '../add-dynamic-pages/new-page/addRouterDataset'
import { ensurePageIsLegacyIndexable } from '../ensurePageIsLegacyIndexable'
import { getFedopsInteractionName } from '../getFedopsInteractionName'
import { prepareDataForBlankPage } from './prepareDataForBlankPage'

export const addBlankDynamicPage = async ({
  editorSdkProxy,
  collectionsApi,
  appApi,
  experimentsManager,
  i18n,
  logger,
  fedopsLogger,
  editorType,
  collectionId,
  biDefaults,
}: {
  editorSdkProxy: EditorSDK
  collectionsApi: CollectionsApi
  appApi: AppApi
  experimentsManager: Experiments
  i18n: I18n
  logger: Logger
  fedopsLogger: FedopsLogger
  editorType: EDITOR_TYPE
  collectionId: string
  biDefaults: DpBiDefaults
}) => {
  const interactionName = getFedopsInteractionName(editorType)(
    'addBlankFromCollection',
  )

  fedopsLogger.interactionStarted(interactionName)

  let pageRef: PageRef

  const progressBar = getProgressBar({
    editorSdkProxy,
    logger,
    totalSteps: 1,
    title: i18n.t('Adding_Dynamic_Pages_Native_Progress_Bar_Title'),
  })

  await progressBar.open()

  try {
    await progressBar.update({
      stepTitle: i18n.t(
        'Adding_Dynamic_Pages_Native_Progress_Bar_Blank_Page_Step_Title',
      ),
    })

    const schema = await collectionsApi.get(collectionId)

    const { routeDefinition, pageTitle, routerPrefix, pageLinks, pattern } =
      await prepareDataForBlankPage({
        editorSdkProxy,
        appApi,
        schema,
        experimentsManager,
        editorType,
      })

    pageRef = await runTransactionWithRetry(editorSdkProxy, async () => {
      const routerRef = await addOrUpdateRouterWithPattern(
        editorSdkProxy,
        routerPrefix,
        pattern,
        routeDefinition,
      )

      const pageRef = await editorSdkProxy.routers.pages.add({
        routerRef,
        pageTitle,
        pageRoles: [routeDefinition.pageRole],
      })

      await ensurePageIsLegacyIndexable({ editorSdkProxy, pageRef })

      await addRouterDataset({
        editorSdkProxy,
        appApi,
        editorType,
        pageRef,
        collectionName: schema.displayName,
        pageType: PAGE_TYPES.ITEM,
      })

      await editorSdkProxy.document.pages.data.update({
        pageRef,
        data: { managingAppDefId: DATA_BINDING },
      })

      return pageRef
    })

    const label = UNDO_REDO_LABELS.ADD_DATA_BINDING_URL

    await editorSdkProxy.history.add({ label })

    await addPageLinkFields({
      editorSdkProxy,
      collectionsApi,
      collectionId,
      pageLinkFieldsToAdd: [
        {
          fieldKey: pageLinks.itemPage.pageLinkFieldKey,
          pattern: pageLinks.itemPage.pattern,
          routerPrefix,
          displayName: pageTitle,
          pageId: pageRef.id,
        },
      ],
    })

    logger.bi(
      dynamicPageCustomRouterCreatePageClicked({
        page_type: 'item',
        collection_id: collectionId,
        collection_name: schema.displayName,
        new_url: pageLinks.itemPage.url,
        is_auto_bound: false,
        new_collection: false,
        pageId: pageRef.id,
        action_type: 'add_dynamic_page',
        collection_type: getCollectionType(schema),
        ...biDefaults,
      }),
    )

    await progressBar.update()
  } finally {
    await progressBar.close()
  }

  await setDynamicPageTypeStateForPage({
    editorSdkProxy,
    pageRef,
    pageType: DYNAMIC_PAGE_TYPES.ITEM_PAGE,
  })

  await openPagesPanelIfNeeded({
    editorSdkProxy,
    origin: biDefaults.origin,
    pageRef,
  })

  await appApi.openInspectorDatasetListPanel()

  fedopsLogger.interactionEnded(interactionName)

  return { pageRefs: [pageRef] }
}
